import {Injectable} from '@angular/core';
import {UserManager, User, WebStorageStateStore} from 'oidc-client-ts';
import {environment} from '../../environments/environment';
import {LocalStorageService} from './local-storage.service';
import {Observable} from 'rxjs';

declare global {
  interface Window {
    spidLogin: (alias?: string) => void;
  }
}

@Injectable({providedIn: 'root'})
export class SpidAuthService {

  private userManager: UserManager;

  constructor(private localStorageService: LocalStorageService) {
    this.userManager = new UserManager({
      authority: environment.spid.authority,
      client_id: environment.spid.clientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/login`,
      response_type: 'code',
      scope: environment.spid.scope,
      automaticSilentRenew: false,
      // sessionStorage (non localStorage): lo stato OIDC (token, state, nonce) è
      // sensibile e non deve persistere oltre il tab — coerente con volatile-session.
      userStore: new WebStorageStateStore({store: sessionStorage}),
    });

    window.spidLogin = (alias?: string) => this.login(alias);
  }

  login(alias?: string): void {
    const extraQueryParams: Record<string, string> = {};
    if (alias) {
      extraQueryParams['kc_idp_hint'] = alias;
    }
    this.userManager.signinRedirect({extraQueryParams});
  }

  async completeLogin(): Promise<User> {
    return this.userManager.signinRedirectCallback();
  }

  saveSpidTokens$(user: User): Observable<any> {
    const accessToken = user.access_token;
    const refreshToken = user.refresh_token || '';
    const expiresIn = user.expires_in || 3600;
    return this.localStorageService.setSpidAuth$(accessToken, refreshToken, expiresIn);
  }

  async logout(): Promise<void> {
    try {
      await this.userManager.signoutRedirect();
    } catch (e) {
      console.error('[SpidAuth] signoutRedirect failed', e);
    }
  }

  /**
   * Rimuove l'utente OIDC salvato localmente da oidc-client-ts, SENZA contattare Keycloak.
   * Serve dopo il collegamento: l'utente temporaneo è già cancellato, quindi un
   * `signoutRedirect` (che usa l'id_token_hint) non ha più senso: si pulisce solo lo stato locale.
   */
  async rimuoviUtenteLocale(): Promise<void> {
    try {
      await this.userManager.removeUser();
    } catch (e) {
      console.error('[SpidAuth] removeUser failed', e);
    }
  }

  getAccessToken(): string | null {
    return this.localStorageService.getSpidAccessToken();
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.userManager.getUser();
    return !!user && !user.expired;
  }
}
